import { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Typography, Box, Alert, CircularProgress, Button, Paper, IconButton, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip, Menu, MenuItem, Chip, Divider, Avatar, Link, TextField } from '@mui/material';
import { Add as AddIcon, CalendarToday, ContentCopy, Refresh, Star, StarBorder, Business, KeyboardArrowDown, Campaign, Link as LinkIcon, Person, PersonAdd, Email } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import EventsList from '@components/events/EventsList';
import PendingVolunteers from '@components/volunteers/PendingVolunteers';
import VolunteerStatusCard from '@components/volunteers/VolunteerStatusCard';
import InviteCodeHistory from '@components/organization/InviteCodeHistory';
import EmailVerificationBanner from '@components/common/EmailVerificationBanner';
import { getUpcomingEventsByOrganization } from '@services/eventService';
import { getVolunteersByOrganization, setDefaultOrganization } from '@services/userService';
import { getOrganizationById, regenerateInviteCode } from '@services/organizationService';
import { getUpcomingRegistrationsByVolunteer } from '@services/registrationService';
import { subscribeToAnnouncements } from '@services/announcementService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@services/firebase';

const LINK_ICONS = { signal: '📡', groupme: '💬', discord: '🎮', whatsapp: '📱', slack: '💼', facebook: '📘', telegram: '✈️' };
const getLinkIcon = (url = '', name = '') => {
  const s = (url + name).toLowerCase();
  for (const [k, v] of Object.entries(LINK_ICONS)) if (s.includes(k)) return v;
  return '🔗';
};
const formatAnnDate = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { userData: user, activeOrganizationId, activeOrganizationName, userOrganizations, switchOrganization, refreshUserData } = useAuth();
  const [events, setEvents] = useState([]);
  const [pendingVolunteers, setPendingVolunteers] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedSnackbar, setCopiedSnackbar] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState('');
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [userRegistrations, setUserRegistrations] = useState([]);
  const [orgMenuAnchor, setOrgMenuAnchor] = useState(null);
  const [settingDefault, setSettingDefault] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [communityLinks, setCommunityLinks] = useState([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');

  const isOrgAdmin = user?.userType === 'org-admin' || user?.userType === 'platform-admin';
  const lastFetchRef = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const orgId = activeOrganizationId || user?.organizationId;

      if (!orgId) {
        setError('No organization associated with your account.');
        setLoading(false);
        return;
      }

      // Fetch organization details
      const orgData = await getOrganizationById(orgId);
      if (isOrgAdmin) setOrganization(orgData);
      setCommunityLinks(orgData?.communityLinks || []);

      // Fetch events
      try {
        const fetchedEvents = await getUpcomingEventsByOrganization(orgId);
        setEvents(fetchedEvents);
      } catch (eventsErr) {
        if (user?.userType === 'volunteer' && user?.status === 'pending') {
          console.log('Pending volunteer - events will be available after approval');
          setEvents([]);
        } else {
          throw eventsErr;
        }
      }

      // Fetch pending volunteers (only for org admins)
      if (isOrgAdmin) {
        const volunteers = await getVolunteersByOrganization(orgId);
        const pending = volunteers.filter(v => v.status === 'pending');
        setPendingVolunteers(pending);
      }

      // Fetch user's registrations (only for volunteers)
      if (user?.userType === 'volunteer' && user?.uid) {
        try {
          const registrations = await getUpcomingRegistrationsByVolunteer(user.uid);
          setUserRegistrations(registrations);
        } catch (regErr) {
          console.error('Error fetching registrations:', regErr);
          setUserRegistrations([]);
        }
      }

      lastFetchRef.current = Date.now();
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(`Failed to load data: ${err.message || 'Please try again later.'}`);
    } finally {
      setLoading(false);
    }
  }, [user, activeOrganizationId, isOrgAdmin]);

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh once per day when user returns to the app/tab
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        const oneDayMs = 24 * 60 * 60 * 1000;
        if (Date.now() - lastFetchRef.current > oneDayMs) {
          fetchData();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchData]);

  // Subscribe to announcements in real time
  useEffect(() => {
    const orgId = activeOrganizationId || user?.organizationId;
    if (!orgId || user?.status === 'pending') return;
    const unsub = subscribeToAnnouncements(orgId, setAnnouncements, 5);
    return () => unsub();
  }, [user, activeOrganizationId]);

  // Show welcome message based on user type
  const getWelcomeMessage = () => {
    if (user?.userType === 'org-admin') {
      return `Welcome back, ${user.displayName}!`;
    } else if (user?.userType === 'volunteer') {
      return `Hello, ${user.displayName}!`;
    } else if (user?.userType === 'platform-admin') {
      return `Platform Admin Dashboard - ${user.displayName}`;
    }
    return 'Welcome!';
  };

  // Copy to clipboard
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedMessage(`${label} copied to clipboard!`);
    setCopiedSnackbar(true);
  };

  // Handle org switcher
  const handleOrgMenuOpen = (e) => setOrgMenuAnchor(e.currentTarget);
  const handleOrgMenuClose = () => setOrgMenuAnchor(null);

  const handleSwitchOrg = (org) => {
    switchOrganization(org.organizationId, org.organizationName);
    handleOrgMenuClose();
  };

  const handleSetDefault = async (e, org) => {
    e.stopPropagation();
    try {
      setSettingDefault(true);
      await setDefaultOrganization(user.uid, org.organizationId, org.organizationName);
      await refreshUserData();
      setCopiedMessage(`${org.organizationName} set as default organization`);
      setCopiedSnackbar(true);
    } catch (err) {
      console.error('Error setting default org:', err);
    } finally {
      setSettingDefault(false);
      handleOrgMenuClose();
    }
  };

  // Handle regenerate invite code
  const handleRegenerateInviteCode = async () => {
    try {
      setRegenerating(true);
      const orgId = activeOrganizationId || user.organizationId;
      const newInviteCode = await regenerateInviteCode(
        orgId,
        user.uid,
        user.displayName
      );

      // Update local state
      setOrganization(prev => ({ ...prev, inviteCode: newInviteCode }));

      setRegenerateDialogOpen(false);
      setCopiedMessage('Invite code regenerated successfully!');
      setCopiedSnackbar(true);
    } catch (err) {
      console.error('Error regenerating invite code:', err);
      setError('Failed to regenerate invite code. Please try again.');
    } finally {
      setRegenerating(false);
    }
  };

  // Refresh volunteers list after approval/rejection
  const handleVolunteerUpdate = async () => {
    try {
      const orgId = activeOrganizationId || user.organizationId;
      const volunteers = await getVolunteersByOrganization(orgId);
      const pending = volunteers.filter(v => v.status === 'pending');
      setPendingVolunteers(pending);
    } catch (err) {
      console.error('Error refreshing volunteers:', err);
    }
  };

  const handleSendInvites = async () => {
    const emails = inviteEmails
      .split(/[\n,]+/)
      .map((e) => e.trim())
      .filter((e) => e.includes('@'));
    if (emails.length === 0) {
      setInviteError('Please enter at least one valid email address.');
      return;
    }
    try {
      setInviting(true);
      setInviteError('');
      const orgId = activeOrganizationId || user.organizationId;
      const fn = httpsCallable(functions, 'sendInviteEmail');
      const result = await fn({ emails, organizationId: orgId });
      setInviteDialogOpen(false);
      setInviteEmails('');
      setCopiedMessage(`Invitation${result.data.sent !== 1 ? 's' : ''} sent to ${result.data.sent} address${result.data.sent !== 1 ? 'es' : ''}.`);
      setCopiedSnackbar(true);
    } catch (err) {
      setInviteError(err?.message || 'Failed to send invitations. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              {getWelcomeMessage()}
            </Typography>

            {/* Org Switcher */}
            {userOrganizations.length > 1 ? (
              <Box sx={{ mb: 1 }}>
                <Chip
                  icon={<Business fontSize="small" />}
                  label={activeOrganizationName || 'Select Organization'}
                  onClick={handleOrgMenuOpen}
                  onDelete={handleOrgMenuOpen}
                  deleteIcon={<KeyboardArrowDown />}
                  color="primary"
                  variant="outlined"
                  sx={{ cursor: 'pointer' }}
                />
                <Menu
                  anchorEl={orgMenuAnchor}
                  open={Boolean(orgMenuAnchor)}
                  onClose={handleOrgMenuClose}
                  PaperProps={{ sx: { minWidth: 260 } }}
                >
                  {userOrganizations.map((org) => (
                    <MenuItem
                      key={org.organizationId}
                      selected={org.organizationId === activeOrganizationId}
                      onClick={() => handleSwitchOrg(org)}
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {org.organizationName}
                      </Typography>
                      <Tooltip title={org.organizationId === user?.defaultOrganizationId ? 'Default' : 'Set as default'}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleSetDefault(e, org)}
                          disabled={settingDefault}
                          color={org.organizationId === user?.defaultOrganizationId ? 'warning' : 'default'}
                        >
                          {org.organizationId === user?.defaultOrganizationId ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            ) : (
              (activeOrganizationName || user?.organizationName) && (
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                  {activeOrganizationName || user?.organizationName}
                </Typography>
              )
            )}

            {isOrgAdmin && (activeOrganizationId || user?.organizationId) && (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    bgcolor: 'primary.50',
                    borderColor: 'primary.main',
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Organization ID:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      bgcolor: 'background.paper',
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                    }}
                  >
                    {activeOrganizationId || user.organizationId}
                  </Typography>
                  <Tooltip title="Copy Organization ID">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(activeOrganizationId || user.organizationId, 'Organization ID')}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Paper>

                {organization?.inviteCode && (
                  <Paper
                    variant="outlined"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1,
                      bgcolor: 'success.lighter',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      Invite Code:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        bgcolor: 'background.paper',
                        px: 1,
                        py: 0.5,
                        borderRadius: 0.5,
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        letterSpacing: 1,
                      }}
                    >
                      {organization.inviteCode}
                    </Typography>
                    <Tooltip title="Copy Invite Code">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(organization.inviteCode, 'Invite code')}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Regenerate Invite Code">
                      <IconButton
                        size="small"
                        onClick={() => setRegenerateDialogOpen(true)}
                        color="warning"
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Share the invite code with volunteers to allow them to register for your organization
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonAdd />}
                    onClick={() => { setInviteDialogOpen(true); setInviteError(''); }}
                  >
                    Invite by Email
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          {isOrgAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/events/create')}
              sx={{ minWidth: 150 }}
            >
              Create Event
            </Button>
          )}
        </Box>
      </Box>

      {/* Email Verification Banner */}
      <EmailVerificationBanner />

      {/* Volunteer Status Card (Volunteers Only) */}
      {user?.userType === 'volunteer' && user?.status && (
        <VolunteerStatusCard
          organizationName={activeOrganizationName || user.organizationName || 'Your Organization'}
          status={user.status}
        />
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Pending Volunteers Section (Org Admins Only) */}
          {isOrgAdmin && pendingVolunteers.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <PendingVolunteers
                volunteers={pendingVolunteers}
                onUpdate={handleVolunteerUpdate}
              />
            </Box>
          )}

          {/* Invite Code History (Org Admins Only) */}
          {isOrgAdmin && (activeOrganizationId || user?.organizationId) && (
            <Box sx={{ mb: 4 }}>
              <InviteCodeHistory
                key={organization?.inviteCode}
                organizationId={activeOrganizationId || user.organizationId}
              />
            </Box>
          )}

          {/* Announcements Feed */}
          {announcements.length > 0 && (
            <Paper elevation={2} sx={{ mb: 4, overflow: 'hidden' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Campaign color="primary" fontSize="small" />
                  <Typography variant="h6" fontWeight="bold">Announcements</Typography>
                </Box>
                {isOrgAdmin && (
                  <Button size="small" onClick={() => navigate('/communications')}>Manage</Button>
                )}
              </Box>
              {announcements.map((a, idx) => (
                <Box key={a.id}>
                  {idx > 0 && <Divider />}
                  <Box sx={{ p: 2, display: 'flex', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, flexShrink: 0 }}>
                      <Person sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" fontWeight="bold">{a.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.sentByName} · {formatAnnDate(a.createdAt)}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                        {a.message}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Paper>
          )}

          {/* Community Links */}
          {communityLinks.length > 0 && (
            <Paper elevation={2} sx={{ mb: 4, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <LinkIcon color="action" fontSize="small" />
                <Typography variant="h6" fontWeight="bold">Community Links</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {communityLinks.map((link) => (
                  <Chip
                    key={link.id}
                    label={`${getLinkIcon(link.url, link.name)} ${link.name}`}
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    variant="outlined"
                    title={link.description || link.url}
                  />
                ))}
              </Box>
            </Paper>
          )}

          {/* Upcoming Events Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <CalendarToday color="primary" />
            <Typography variant="h5" fontWeight="bold">
              Upcoming Events
            </Typography>
          </Box>

          {/* Events List */}
          <EventsList
            events={events}
            userRegistrations={userRegistrations}
            emptyMessage={
              isOrgAdmin
                ? 'No events created yet. Click "Create Event" to get started!'
                : user?.status === 'pending'
                ? 'Events will be available once your account is approved by the organization admin.'
                : 'No events available at the moment. Check back soon!'
            }
          />
        </>
      )}

      {/* Copy Confirmation Snackbar */}
      <Snackbar
        open={copiedSnackbar}
        autoHideDuration={3000}
        onClose={() => setCopiedSnackbar(false)}
        message={copiedMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Invite by Email Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => !inviting && setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Volunteers by Email</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Each person will receive a branded invitation email with your organization's invite code and a direct registration link.
          </DialogContentText>
          <TextField
            label="Email addresses"
            placeholder="jane@example.com, john@example.com"
            fullWidth
            multiline
            minRows={3}
            value={inviteEmails}
            onChange={(e) => setInviteEmails(e.target.value)}
            helperText="Separate multiple addresses with commas or new lines"
            disabled={inviting}
          />
          {inviteError && (
            <Alert severity="error" sx={{ mt: 2 }}>{inviteError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)} disabled={inviting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendInvites}
            disabled={inviting}
            startIcon={inviting ? <CircularProgress size={18} /> : <Email />}
          >
            {inviting ? 'Sending…' : 'Send Invitations'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate Invite Code Confirmation Dialog */}
      <Dialog
        open={regenerateDialogOpen}
        onClose={() => setRegenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Regenerate Invite Code?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to regenerate the invite code? The current code{' '}
            <strong>{organization?.inviteCode}</strong> will no longer work, and you'll need to
            share the new code with volunteers.
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action will invalidate any existing invite links or codes you've shared.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateDialogOpen(false)} disabled={regenerating}>
            Cancel
          </Button>
          <Button
            onClick={handleRegenerateInviteCode}
            variant="contained"
            color="warning"
            disabled={regenerating}
            startIcon={regenerating ? <CircularProgress size={20} /> : <Refresh />}
          >
            {regenerating ? 'Regenerating...' : 'Regenerate Code'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DashboardPage;
